import posthog from 'posthog-js'
import { POSTHOG_EVENTS, POSTHOG_PROPERTIES } from '../constants/posthogEvents'

// PostHog wrapper utility to replace CleverTap calls
const PostHogTracker = {
  // Page tracking
  pageViewed: (pageName, properties = {}) => {
    posthog.capture('Page Viewed', {
      page_name: pageName,
      ...properties,
    })
  },

  // Button clicks
  buttonClicked: (buttonName, properties = {}) => {
    posthog.capture('Button Clicked', {
      button_name: buttonName,
      ...properties,
    })
  },

  // Link clicks
  linkClicked: (linkName, properties = {}) => {
    posthog.capture('Link Clicked', {
      link_name: linkName,
      ...properties,
    })
  },

  // Navigation tracking
  navigationClicked: (navItem, properties = {}) => {
    posthog.capture('Navigation Clicked', {
      nav_item: navItem,
      ...properties,
    })
  },

  // Generic event tracking (for custom events)
  track: (eventName, properties = {}) => {
    posthog.capture(eventName, properties)
  },

  // User identification (replaces clevertap.onUserLogin.push)
  identifyUser: (userId, userProperties = {}) => {
    posthog.identify(userId, userProperties)
    // Set user properties in People profile (required for person to show up in PostHog)
    // Using modern setPersonProperties API instead of deprecated people.set
    if (Object.keys(userProperties).length > 0) {
      posthog.setPersonProperties(userProperties)
    }
  },

  // Enhanced user identification with automatic super properties
  identifyUserWithSession: (userId, userProperties = {}, sessionProperties = {}) => {
    // Identify the user
    posthog.identify(userId, userProperties)

    // Set user properties in People profile (required for person to show up in PostHog)
    // Using modern setPersonProperties API instead of deprecated people.set
    if (Object.keys(userProperties).length > 0) {
      posthog.setPersonProperties(userProperties)
    }

    // Register session-level super properties
    if (Object.keys(sessionProperties).length > 0) {
      PostHogTracker.registerSuperProperties(sessionProperties)
    }

    // Extract and register UTM data if available
    if (window.utmData) {
      const utmProperties = PostHogTracker.extractUTMProperties(window.utmData)
      if (Object.keys(utmProperties).length > 0) {
        PostHogTracker.registerSuperPropertiesOnce(utmProperties)
      }
    }
  },

  // User profile updates (replaces clevertap.profile.push)
  updateUserProfile: (properties) => {
    // Using modern setPersonProperties API instead of deprecated people.set
    posthog.setPersonProperties(properties)
  },

  // Alias for user (when transitioning from anonymous to identified)
  alias: (alias) => {
    posthog.alias(alias)
  },

  // Set user properties once
  setUserPropertiesOnce: (properties) => {
    // Using modern setPersonProperties API with $set_once parameter
    posthog.setPersonProperties({}, properties)
  },

  // Group users (for organization/company tracking)
  group: (groupType, groupKey, groupProperties = {}) => {
    posthog.group(groupType, groupKey, groupProperties)
  },

  // Register super properties (sent with every event)
  registerSuperProperties: (properties) => {
    posthog.register(properties)
  },

  // Register super properties once (won't overwrite existing)
  registerSuperPropertiesOnce: (properties) => {
    posthog.register_once(properties)
  },

  // Unregister a super property
  unregisterSuperProperty: (propertyName) => {
    posthog.unregister(propertyName)
  },

  // Extract UTM properties from window.utmData or URL params
  extractUTMProperties: (utmData = {}) => {
    const utmProps = {}

    // Map common UTM fields
    if (utmData.utm_source) utmProps.utm_source = utmData.utm_source
    if (utmData.utm_medium) utmProps.utm_medium = utmData.utm_medium
    if (utmData.utm_campaign) utmProps.utm_campaign = utmData.utm_campaign
    if (utmData.utm_term) utmProps.utm_term = utmData.utm_term
    if (utmData.utm_content) utmProps.utm_content = utmData.utm_content

    // Map custom marketing fields
    if (utmData.$marketing_title) utmProps.marketing_title = utmData.$marketing_title
    if (utmData.site_url) utmProps.landing_page = utmData.site_url

    return utmProps
  },

  // Get user type from user details
  getUserType: (userDetails) => {
    if (!userDetails) return 'unknown'
    if (!userDetails['custom:role']) return 'customer'

    const role = userDetails['custom:role']
    if (role.includes('PARTNER')) return 'partner'
    if (role.includes('IPO')) return 'ipo_user'
    if (role.includes('CUSTOMER')) return 'customer'
    return 'other'
  },

  // Reset user (on logout)
  reset: () => {
    posthog.reset()
  },

  // Helper methods for common patterns

  // Auth events
  loginAttempted: (method = 'unknown') => {
    PostHogTracker.track('Login Attempted', { method })
  },

  loginCompleted: (userId, userDetails = {}, method = 'unknown') => {
    // Build comprehensive user properties
    const userProps = {
      email: userDetails.email,
      phone_number: userDetails.phoneNumber,
      first_name: userDetails.firstName,
      last_name: userDetails.lastName,
      name: `${userDetails.firstName} ${userDetails.lastName}`,
    }

    // Build session super properties
    const sessionProps = {
      user_type: PostHogTracker.getUserType(userDetails),
      login_method: method,
    }

    // Add partner info if available
    if (userDetails.partner) {
      sessionProps.partner = userDetails.partner
    }

    // Use enhanced identification with super properties
    PostHogTracker.identifyUserWithSession(userId, userProps, sessionProps)
    PostHogTracker.track('Login Completed', { method })
  },

  signupAttempted: (method = 'unknown') => {
    PostHogTracker.track('Signup Attempted', { method })
  },

  signupCompleted: (userId, userDetails = {}, method = 'unknown', source = null) => {
    // Build comprehensive user properties
    const userProps = {
      email: userDetails.email,
      phone_number: userDetails.phoneNumber,
      first_name: userDetails.firstName,
      last_name: userDetails.lastName,
      name: `${userDetails.firstName} ${userDetails.lastName}`,
    }

    // Build session super properties
    const sessionProps = {
      user_type: PostHogTracker.getUserType(userDetails),
      signup_method: method,
    }

    // Add source if provided
    if (source) {
      sessionProps.signup_source = source
      PostHogTracker.setUserPropertiesOnce({ initial_source: source })
    }

    // Add partner info if available
    if (userDetails.partner || userDetails.isPartnerUser) {
      sessionProps.partner = userDetails.partner || 'true'
      sessionProps.user_type = 'partner'
    }

    // Use enhanced identification with super properties
    PostHogTracker.identifyUserWithSession(userId, userProps, sessionProps)
    PostHogTracker.track('Signup Completed', { method, source })
  },

  otpEntered: (context = 'unknown') => {
    PostHogTracker.track('OTP Entered', { context })
  },

  otpFailure: (context = 'unknown', reason = 'unknown') => {
    PostHogTracker.track('OTP Failed', { context, reason })
  },

  // Signup form events
  firstNameEntered: (pageName = 'Sign Up') => {
    PostHogTracker.track(POSTHOG_EVENTS.FIRST_NAME_ENTERED, {
      [POSTHOG_PROPERTIES.PAGE_NAME]: pageName,
    })
  },

  lastNameEntered: (pageName = 'Sign Up') => {
    PostHogTracker.track(POSTHOG_EVENTS.LAST_NAME_ENTERED, {
      [POSTHOG_PROPERTIES.PAGE_NAME]: pageName,
    })
  },

  phoneNumberEntered: (pageName = 'unknown') => {
    PostHogTracker.track(POSTHOG_EVENTS.PHONE_NUMBER_ENTERED, {
      [POSTHOG_PROPERTIES.PAGE_NAME]: pageName,
    })
  },

  termsAndConditionsClicked: (pageName = 'Sign Up') => {
    PostHogTracker.track(POSTHOG_EVENTS.TERMS_AND_CONDITIONS_CLICKED, {
      [POSTHOG_PROPERTIES.PAGE_NAME]: pageName,
    })
  },

  signupOtpEntered: () => {
    PostHogTracker.track(POSTHOG_EVENTS.SIGNUP_OTP_ENTERED, {
      [POSTHOG_PROPERTIES.PAGE_NAME]: 'Signup OTP',
    })
  },

  signupOtpFailed: (reason = 'unknown') => {
    PostHogTracker.track(POSTHOG_EVENTS.SIGNUP_OTP_FAILED, {
      reason,
    })
  },

  firstLoginAfterSignup: (userId) => {
    PostHogTracker.track(POSTHOG_EVENTS.FIRST_LOGIN_AFTER_SIGNUP, {
      [POSTHOG_PROPERTIES.USER_ID]: userId,
    })
  },

  logout: (pageName = 'unknown') => {
    PostHogTracker.track(POSTHOG_EVENTS.LOGOUT, {
      [POSTHOG_PROPERTIES.PAGE_NAME]: pageName,
    })
  },

  // SIP registration events
  sipRegistrationStarted: (fundName, amount, sipDate) => {
    PostHogTracker.track(POSTHOG_EVENTS.SIP_REGISTRATION_STARTED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.SIP_DATE]: sipDate,
    })
  },

  sipRegistrationSuccess: (fundName, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.SIP_REGISTRATION_SUCCESS, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  sipRegistrationFailed: (fundName, reason) => {
    PostHogTracker.track(POSTHOG_EVENTS.SIP_REGISTRATION_FAILED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: reason,
    })
  },

  sipRegistrationError: (fundName, reason) => {
    PostHogTracker.track(POSTHOG_EVENTS.SIP_REGISTRATION_ERROR, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: reason,
    })
  },

  // Financial events
  investmentInitiated: (fundName, amount, type = 'lumpsum') => {
    PostHogTracker.track('Investment Initiated', {
      fund_name: fundName,
      amount,
      investment_type: type,
    })
  },

  sipStarted: (fundName, monthlyAmount, date) => {
    PostHogTracker.track('SIP Started', {
      fund_name: fundName,
      monthly_amount: monthlyAmount,
      sip_date: date,
    })
  },

  redemptionInitiated: (fundName, amount, units) => {
    PostHogTracker.track('Redemption Initiated', {
      fund_name: fundName,
      amount,
      units,
    })
  },

  // Calculator events
  calculatorViewed: (calculatorType) => {
    PostHogTracker.pageViewed(`${calculatorType} Calculator`)
  },

  // Support events
  supportTicketCreated: (category = 'general') => {
    PostHogTracker.track('Support Ticket Created', { category })
  },

  faqClicked: (question) => {
    PostHogTracker.track('FAQ Clicked', { question })
  },

  // ============ INTRO & ONBOARDING ============

  introVideoPlayed: () => {
    PostHogTracker.track(POSTHOG_EVENTS.INTRO_VIDEO_PLAYED, {})
  },

  introVideoCompleted: () => {
    PostHogTracker.track(POSTHOG_EVENTS.INTRO_VIDEO_COMPLETED, {})
  },

  introVideoSkipped: () => {
    PostHogTracker.track(POSTHOG_EVENTS.INTRO_VIDEO_SKIPPED, {})
  },

  questionnaireStarted: () => {
    PostHogTracker.track(POSTHOG_EVENTS.QUESTIONNAIRE_STARTED, {})
  },

  questionnaireQuestionAnswered: (questionNumber, questionText, answer) => {
    PostHogTracker.track(POSTHOG_EVENTS.QUESTIONNAIRE_QUESTION_ANSWERED, {
      question_number: questionNumber,
      question_text: questionText,
      answer: answer,
    })
  },

  questionnaireCompleted: (totalQuestions, completionTime = null) => {
    PostHogTracker.track(POSTHOG_EVENTS.QUESTIONNAIRE_COMPLETED, {
      total_questions: totalQuestions,
      completion_time: completionTime,
    })
  },

  actionPlanViewed: (planId, planName, planType) => {
    PostHogTracker.track(POSTHOG_EVENTS.ACTION_PLAN_VIEWED, {
      [POSTHOG_PROPERTIES.PLAN_ID]: planId,
      [POSTHOG_PROPERTIES.PLAN_NAME]: planName,
      plan_type: planType,
    })
  },

  // ============ KYC FLOW ============

  kycInitiated: () => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_INITIATED, {})
  },

  kycPanEntered: (panNumber) => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_PAN_ENTERED, {
      [POSTHOG_PROPERTIES.PAN_NUMBER]: panNumber,
    })
  },

  kycDobEntered: (dob) => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_DOB_ENTERED, {
      [POSTHOG_PROPERTIES.DOB]: dob,
    })
  },

  kycTermsAccepted: () => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_TERMS_ACCEPTED, {})
  },

  kycHyperVergeLaunched: (mobileNumber) => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_HYPERVERGE_LAUNCHED, {
      [POSTHOG_PROPERTIES.PHONE_NUMBER]: mobileNumber,
    })
  },

  kycDocumentUploaded: (documentType) => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_DOCUMENT_UPLOADED, {
      document_type: documentType,
    })
  },

  kycFaceVerified: () => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_FACE_VERIFIED, {})
  },

  kycCompleted: (status, applicationStatus) => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_COMPLETED, {
      [POSTHOG_PROPERTIES.KYC_STATUS]: status,
      application_status: applicationStatus,
    })
  },

  kycFailed: (reason) => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_FAILED, {
      [POSTHOG_PROPERTIES.KYC_REASON]: reason,
    })
  },

  kycDeclined: (reason) => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_DECLINED, {
      [POSTHOG_PROPERTIES.KYC_REASON]: reason,
    })
  },

  kycCancelled: () => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_CANCELLED, {})
  },

  elogInitiated: (clientCode) => {
    PostHogTracker.track(POSTHOG_EVENTS.ELOG_INITIATED, {
      client_code: clientCode,
    })
  },

  elogAuthenticationStarted: (authUrl) => {
    PostHogTracker.track(POSTHOG_EVENTS.ELOG_AUTHENTICATION_STARTED, {
      auth_url: authUrl ? 'provided' : 'missing',
    })
  },

  elogCompleted: (status) => {
    PostHogTracker.track(POSTHOG_EVENTS.ELOG_COMPLETED, {
      [POSTHOG_PROPERTIES.ELOG_STATUS]: status,
    })
  },

  elogFailed: (reason) => {
    PostHogTracker.track(POSTHOG_EVENTS.ELOG_FAILED, {
      reason: reason,
    })
  },

  phoneNumberMismatchDetected: (kycPhone, loginPhone) => {
    PostHogTracker.track(POSTHOG_EVENTS.PHONE_NUMBER_MISMATCH_DETECTED, {
      kyc_phone: kycPhone,
      login_phone: loginPhone,
    })
  },

  primaryPhoneUpdated: (oldPhone, newPhone) => {
    PostHogTracker.track(POSTHOG_EVENTS.PRIMARY_PHONE_UPDATED, {
      old_phone: oldPhone,
      new_phone: newPhone,
    })
  },

  // ============ MUTUAL FUND BROWSING ============

  mfPageViewed: (assetClass = 'all') => {
    PostHogTracker.track(POSTHOG_EVENTS.MF_PAGE_VIEWED, {
      [POSTHOG_PROPERTIES.ASSET_CLASS]: assetClass,
    })
  },

  fundSearchInitiated: (searchTerm) => {
    PostHogTracker.track(POSTHOG_EVENTS.FUND_SEARCH_INITIATED, {
      search_term: searchTerm,
    })
  },

  fundFiltered: (filterType, filterValue) => {
    PostHogTracker.track(POSTHOG_EVENTS.FUND_FILTERED, {
      [POSTHOG_PROPERTIES.FILTER_TYPE]: filterType,
      filter_value: filterValue,
    })
  },

  fundSorted: (sortBy, sortDirection) => {
    PostHogTracker.track(POSTHOG_EVENTS.FUND_SORTED, {
      [POSTHOG_PROPERTIES.SORT_BY]: sortBy,
      sort_direction: sortDirection,
    })
  },

  fundCategorySelected: (category) => {
    PostHogTracker.track(POSTHOG_EVENTS.FUND_CATEGORY_SELECTED, {
      [POSTHOG_PROPERTIES.CATEGORY]: category,
    })
  },

  fundCardClicked: (fundName, fundId) => {
    PostHogTracker.track(POSTHOG_EVENTS.FUND_CARD_CLICKED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.FUND_ID]: fundId,
    })
  },

  fundDetailsViewed: (fundName, fundId, assetClass) => {
    PostHogTracker.track(POSTHOG_EVENTS.FUND_DETAILS_VIEWED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.FUND_ID]: fundId,
      [POSTHOG_PROPERTIES.ASSET_CLASS]: assetClass,
    })
  },

  fundBrowsed: (fundName, fundId) => {
    PostHogTracker.track(POSTHOG_EVENTS.FUND_BROWSED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.FUND_ID]: fundId,
    })
  },

  performanceChartViewed: (fundId, period) => {
    PostHogTracker.track(POSTHOG_EVENTS.PERFORMANCE_CHART_VIEWED, {
      [POSTHOG_PROPERTIES.FUND_ID]: fundId,
      period: period,
    })
  },

  // ============ INVESTMENT FLOW ============

  investmentTypeSelected: (type, fundName = null) => {
    PostHogTracker.track(POSTHOG_EVENTS.INVESTMENT_TYPE_SELECTED, {
      [POSTHOG_PROPERTIES.INVESTMENT_TYPE]: type,
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
    })
  },

  lumpsumAmountEntered: (amount, fundName) => {
    PostHogTracker.track(POSTHOG_EVENTS.LUMPSUM_AMOUNT_ENTERED, {
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
    })
  },

  sipAmountEntered: (amount, fundName) => {
    PostHogTracker.track(POSTHOG_EVENTS.SIP_AMOUNT_ENTERED, {
      [POSTHOG_PROPERTIES.SIP_AMOUNT]: amount,
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
    })
  },

  sipDateSelected: (date, fundName) => {
    PostHogTracker.track(POSTHOG_EVENTS.SIP_DATE_SELECTED, {
      [POSTHOG_PROPERTIES.SIP_DATE]: date,
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
    })
  },

  amountEntered: (amount, type = 'unknown') => {
    PostHogTracker.track(POSTHOG_EVENTS.AMOUNT_ENTERED, {
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.INVESTMENT_TYPE]: type,
    })
  },

  // ============ MANDATE & ORDER ============

  mandateCheckInitiated: (amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.MANDATE_CHECK_INITIATED, {
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  newMandateRequired: (amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.NEW_MANDATE_REQUIRED, {
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  existingMandateFound: (mandateId, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.EXISTING_MANDATE_FOUND, {
      [POSTHOG_PROPERTIES.MANDATE_ID]: mandateId,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  mandateSetupInitiated: (amount, bankName) => {
    PostHogTracker.track(POSTHOG_EVENTS.MANDATE_SETUP_INITIATED, {
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.BANK_NAME]: bankName,
    })
  },

  bankAuthenticationStarted: (bankName) => {
    PostHogTracker.track(POSTHOG_EVENTS.BANK_AUTHENTICATION_STARTED, {
      [POSTHOG_PROPERTIES.BANK_NAME]: bankName,
    })
  },

  mandateConfirmed: (mandateId, amount, status) => {
    PostHogTracker.track(POSTHOG_EVENTS.MANDATE_CONFIRMED, {
      [POSTHOG_PROPERTIES.MANDATE_ID]: mandateId,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.MANDATE_STATUS]: status,
    })
  },

  orderValidationSuccess: (fundName, amount, type) => {
    PostHogTracker.track(POSTHOG_EVENTS.ORDER_VALIDATION_SUCCESS, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.INVESTMENT_TYPE]: type,
    })
  },

  orderValidationFailed: (fundName, amount, reason) => {
    PostHogTracker.track(POSTHOG_EVENTS.ORDER_VALIDATION_FAILED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: reason,
    })
  },

  // ============ PAYMENT ============

  paymentMethodSelected: (method, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.PAYMENT_METHOD_SELECTED, {
      [POSTHOG_PROPERTIES.PAYMENT_METHOD]: method,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  paymentUpiSelected: (upiId, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.PAYMENT_UPI_SELECTED, {
      [POSTHOG_PROPERTIES.UPI_ID]: upiId,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  paymentNetBankingSelected: (bankName, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.PAYMENT_NETBANKING_SELECTED, {
      [POSTHOG_PROPERTIES.BANK_NAME]: bankName,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  paymentInitiated: (orderNumber, amount, method) => {
    PostHogTracker.track(POSTHOG_EVENTS.PAYMENT_INITIATED, {
      [POSTHOG_PROPERTIES.ORDER_NUMBER]: orderNumber,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.PAYMENT_METHOD]: method,
    })
  },

  paymentPollingStarted: (orderNumber) => {
    PostHogTracker.track(POSTHOG_EVENTS.PAYMENT_POLLING_STARTED, {
      [POSTHOG_PROPERTIES.ORDER_NUMBER]: orderNumber,
    })
  },

  paymentCompleted: (orderNumber, amount, method, transactionId = null) => {
    PostHogTracker.track(POSTHOG_EVENTS.PAYMENT_COMPLETED, {
      [POSTHOG_PROPERTIES.ORDER_NUMBER]: orderNumber,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.PAYMENT_METHOD]: method,
      [POSTHOG_PROPERTIES.TRANSACTION_ID]: transactionId,
    })
  },

  paymentSuccess: (orderNumber, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.PAYMENT_SUCCESS, {
      [POSTHOG_PROPERTIES.ORDER_NUMBER]: orderNumber,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  paymentFailed: (orderNumber, amount, reason) => {
    PostHogTracker.track(POSTHOG_EVENTS.PAYMENT_FAILED, {
      [POSTHOG_PROPERTIES.ORDER_NUMBER]: orderNumber,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: reason,
    })
  },

  investmentConfirmationViewed: (fundName, amount, type) => {
    PostHogTracker.track(POSTHOG_EVENTS.INVESTMENT_CONFIRMATION_VIEWED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.INVESTMENT_TYPE]: type,
    })
  },

  // ============ ACTION PLANS / BASKETS ============

  actionCenterClicked: () => {
    PostHogTracker.track(POSTHOG_EVENTS.ACTION_CENTER_CLICKED, {})
  },

  actionPlanListViewed: (planCount) => {
    PostHogTracker.track(POSTHOG_EVENTS.ACTION_PLAN_LIST_VIEWED, {
      plan_count: planCount,
    })
  },

  actionPlanCardClicked: (planId, planName) => {
    PostHogTracker.track(POSTHOG_EVENTS.ACTION_PLAN_CARD_CLICKED, {
      [POSTHOG_PROPERTIES.PLAN_ID]: planId,
      [POSTHOG_PROPERTIES.PLAN_NAME]: planName,
    })
  },

  actionPlanDetailsViewed: (planId, planName, fundCount) => {
    PostHogTracker.track(POSTHOG_EVENTS.ACTION_PLAN_DETAILS_VIEWED, {
      [POSTHOG_PROPERTIES.PLAN_ID]: planId,
      [POSTHOG_PROPERTIES.PLAN_NAME]: planName,
      fund_count: fundCount,
    })
  },

  sipInvestmentOptionSelected: (planId, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.SIP_INVESTMENT_OPTION_SELECTED, {
      [POSTHOG_PROPERTIES.PLAN_ID]: planId,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  onetimeInvestmentOptionSelected: (planId, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.ONETIME_INVESTMENT_OPTION_SELECTED, {
      [POSTHOG_PROPERTIES.PLAN_ID]: planId,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  fundBreakdownViewed: (planId, fundCount) => {
    PostHogTracker.track(POSTHOG_EVENTS.FUND_BREAKDOWN_VIEWED, {
      [POSTHOG_PROPERTIES.PLAN_ID]: planId,
      fund_count: fundCount,
    })
  },

  basketSipSetupInitiated: (planId, amount, sipDate) => {
    PostHogTracker.track(POSTHOG_EVENTS.BASKET_SIP_SETUP_INITIATED, {
      [POSTHOG_PROPERTIES.PLAN_ID]: planId,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.SIP_DATE]: sipDate,
    })
  },

  basketOnetimeSetupInitiated: (planId, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.BASKET_ONETIME_SETUP_INITIATED, {
      [POSTHOG_PROPERTIES.PLAN_ID]: planId,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  basketOrderPlaced: (planId, amount, type) => {
    PostHogTracker.track(POSTHOG_EVENTS.BASKET_ORDER_PLACED, {
      [POSTHOG_PROPERTIES.PLAN_ID]: planId,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      order_type: type,
    })
  },

  subsequentInvestmentInitiated: (planId, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.SUBSEQUENT_INVESTMENT_INITIATED, {
      [POSTHOG_PROPERTIES.PLAN_ID]: planId,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  // ============ PORTFOLIO MANAGEMENT ============

  portfolioSummaryViewed: (totalValue, totalInvested, returns) => {
    PostHogTracker.track(POSTHOG_EVENTS.PORTFOLIO_SUMMARY_VIEWED, {
      total_value: totalValue,
      total_invested: totalInvested,
      returns: returns,
    })
  },

  holdingsTabClicked: () => {
    PostHogTracker.track(POSTHOG_EVENTS.HOLDINGS_TAB_CLICKED, {})
  },

  sipsTabClicked: () => {
    PostHogTracker.track(POSTHOG_EVENTS.SIPS_TAB_CLICKED, {})
  },

  ordersTabClicked: () => {
    PostHogTracker.track(POSTHOG_EVENTS.ORDERS_TAB_CLICKED, {})
  },

  individualHoldingClicked: (fundName, currentValue) => {
    PostHogTracker.track(POSTHOG_EVENTS.INDIVIDUAL_HOLDING_CLICKED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      current_value: currentValue,
    })
  },

  basketHoldingClicked: (basketName, currentValue) => {
    PostHogTracker.track(POSTHOG_EVENTS.BASKET_HOLDING_CLICKED, {
      [POSTHOG_PROPERTIES.BASKET_NAME]: basketName,
      current_value: currentValue,
    })
  },

  portfolioViewed: () => {
    PostHogTracker.track(POSTHOG_EVENTS.PORTFOLIO_VIEWED, {})
  },

  // ============ REDEMPTION ============

  redeemButtonClicked: (fundName, currentValue) => {
    PostHogTracker.track(POSTHOG_EVENTS.REDEEM_BUTTON_CLICKED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      current_value: currentValue,
    })
  },

  investMoreClicked: (fundName) => {
    PostHogTracker.track(POSTHOG_EVENTS.INVEST_MORE_CLICKED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
    })
  },

  redemptionTypeSelected: (type) => {
    PostHogTracker.track(POSTHOG_EVENTS.REDEMPTION_TYPE_SELECTED, {
      [POSTHOG_PROPERTIES.REDEMPTION_TYPE]: type,
    })
  },

  redemptionAmountEntered: (amount, fundName) => {
    PostHogTracker.track(POSTHOG_EVENTS.REDEMPTION_AMOUNT_ENTERED, {
      [POSTHOG_PROPERTIES.REDEMPTION_AMOUNT]: amount,
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
    })
  },

  fullRedemptionSelected: (fundName, units) => {
    PostHogTracker.track(POSTHOG_EVENTS.FULL_REDEMPTION_SELECTED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.UNITS]: units,
    })
  },

  partialRedemptionSelected: (fundName, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.PARTIAL_REDEMPTION_SELECTED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  redeemOtpVerified: (fundName) => {
    PostHogTracker.track(POSTHOG_EVENTS.REDEEM_OTP_VERIFIED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
    })
  },

  redemptionOrderPlaced: (fundName, amount, units, type) => {
    PostHogTracker.track(POSTHOG_EVENTS.REDEMPTION_ORDER_PLACED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
      [POSTHOG_PROPERTIES.UNITS]: units,
      [POSTHOG_PROPERTIES.REDEMPTION_TYPE]: type,
    })
  },

  redemptionSuccess: (fundName, amount) => {
    PostHogTracker.track(POSTHOG_EVENTS.REDEMPTION_SUCCESS, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.AMOUNT]: amount,
    })
  },

  redemptionFailed: (fundName, reason) => {
    PostHogTracker.track(POSTHOG_EVENTS.REDEMPTION_FAILED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: reason,
    })
  },

  // ============ SIP MANAGEMENT ============

  sipCancelInitiated: (fundName, sipAmount) => {
    PostHogTracker.track(POSTHOG_EVENTS.SIP_CANCEL_INITIATED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.SIP_AMOUNT]: sipAmount,
    })
  },

  sipCancelled: (fundName, sipRegNumber) => {
    PostHogTracker.track(POSTHOG_EVENTS.SIP_CANCELLED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      sip_registration_number: sipRegNumber,
    })
  },

  sipCancelFailed: (fundName, reason) => {
    PostHogTracker.track(POSTHOG_EVENTS.SIP_CANCEL_FAILED, {
      [POSTHOG_PROPERTIES.FUND_NAME]: fundName,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: reason,
    })
  },

  // ============ ORDERS ============

  orderFilterApplied: (filterType, filterValue) => {
    PostHogTracker.track(POSTHOG_EVENTS.ORDER_FILTER_APPLIED, {
      filter_type: filterType,
      filter_value: filterValue,
    })
  },

  orderHistoryExported: (format, recordCount) => {
    PostHogTracker.track(POSTHOG_EVENTS.ORDER_HISTORY_EXPORTED, {
      format: format,
      record_count: recordCount,
    })
  },

  // ============ ERROR TRACKING ============

  apiError: (endpoint, errorCode, errorMessage, statusCode = null) => {
    PostHogTracker.track(POSTHOG_EVENTS.API_ERROR, {
      endpoint: endpoint,
      [POSTHOG_PROPERTIES.ERROR_CODE]: errorCode,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: errorMessage,
      status_code: statusCode,
      [POSTHOG_PROPERTIES.ERROR_TYPE]: 'api',
    })
  },

  paymentError: (orderNumber, errorCode, errorMessage) => {
    PostHogTracker.track(POSTHOG_EVENTS.PAYMENT_ERROR, {
      [POSTHOG_PROPERTIES.ORDER_NUMBER]: orderNumber,
      [POSTHOG_PROPERTIES.ERROR_CODE]: errorCode,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: errorMessage,
      [POSTHOG_PROPERTIES.ERROR_TYPE]: 'payment',
    })
  },

  kycError: (stage, errorCode, errorMessage) => {
    PostHogTracker.track(POSTHOG_EVENTS.KYC_ERROR, {
      kyc_stage: stage,
      [POSTHOG_PROPERTIES.ERROR_CODE]: errorCode,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: errorMessage,
      [POSTHOG_PROPERTIES.ERROR_TYPE]: 'kyc',
    })
  },

  formValidationError: (formName, fieldName, errorMessage) => {
    PostHogTracker.track(POSTHOG_EVENTS.FORM_VALIDATION_ERROR, {
      form_name: formName,
      field_name: fieldName,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: errorMessage,
      [POSTHOG_PROPERTIES.ERROR_TYPE]: 'validation',
    })
  },

  tokenExpiredError: (action) => {
    PostHogTracker.track(POSTHOG_EVENTS.TOKEN_EXPIRED_ERROR, {
      action: action,
      [POSTHOG_PROPERTIES.ERROR_TYPE]: 'auth',
    })
  },

  networkError: (action, errorMessage) => {
    PostHogTracker.track(POSTHOG_EVENTS.NETWORK_ERROR, {
      action: action,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: errorMessage,
      [POSTHOG_PROPERTIES.ERROR_TYPE]: 'network',
    })
  },

  componentError: (componentName, errorMessage, errorStack = null) => {
    PostHogTracker.track(POSTHOG_EVENTS.COMPONENT_ERROR, {
      component_name: componentName,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: errorMessage,
      error_stack: errorStack,
      [POSTHOG_PROPERTIES.ERROR_TYPE]: 'component',
    })
  },

  // ============ SUPPORT ============

  supportPortalViewed: () => {
    PostHogTracker.track(POSTHOG_EVENTS.SUPPORT_PORTAL_VIEWED, {})
  },

  supportQueryTypeSelected: (queryType) => {
    PostHogTracker.track(POSTHOG_EVENTS.SUPPORT_QUERY_TYPE_SELECTED, {
      [POSTHOG_PROPERTIES.QUERY_TYPE]: queryType,
    })
  },

  supportProblemDescribed: (queryType, problemLength) => {
    PostHogTracker.track(POSTHOG_EVENTS.SUPPORT_PROBLEM_DESCRIBED, {
      [POSTHOG_PROPERTIES.QUERY_TYPE]: queryType,
      problem_length: problemLength,
    })
  },

  supportTicketSubmitted: (queryType, ticketId = null) => {
    PostHogTracker.track(POSTHOG_EVENTS.SUPPORT_TICKET_SUBMITTED, {
      [POSTHOG_PROPERTIES.QUERY_TYPE]: queryType,
      [POSTHOG_PROPERTIES.TICKET_ID]: ticketId,
    })
  },

  supportTicketSuccess: (ticketId) => {
    PostHogTracker.track(POSTHOG_EVENTS.SUPPORT_TICKET_SUCCESS, {
      [POSTHOG_PROPERTIES.TICKET_ID]: ticketId,
    })
  },

  supportTicketFailed: (reason) => {
    PostHogTracker.track(POSTHOG_EVENTS.SUPPORT_TICKET_FAILED, {
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: reason,
    })
  },

  faqOpened: (faqQuestion) => {
    PostHogTracker.track(POSTHOG_EVENTS.FAQ_OPENED, {
      [POSTHOG_PROPERTIES.FAQ_QUESTION]: faqQuestion,
    })
  },

  faqSearchUsed: (searchTerm) => {
    PostHogTracker.track(POSTHOG_EVENTS.FAQ_SEARCH_USED, {
      search_term: searchTerm,
    })
  },

  moreHelpClicked: () => {
    PostHogTracker.track(POSTHOG_EVENTS.MORE_HELP_CLICKED, {})
  },

  // ============ REFERRAL ============

  referralCodeViewed: (referralCode) => {
    PostHogTracker.track(POSTHOG_EVENTS.REFERRAL_CODE_VIEWED, {
      [POSTHOG_PROPERTIES.REFERRAL_CODE]: referralCode,
    })
  },

  referralCodeCopied: (referralCode) => {
    PostHogTracker.track(POSTHOG_EVENTS.REFERRAL_CODE_COPIED, {
      [POSTHOG_PROPERTIES.REFERRAL_CODE]: referralCode,
    })
  },

  referralLinkShared: (referralCode, shareMethod) => {
    PostHogTracker.track(POSTHOG_EVENTS.REFERRAL_LINK_SHARED, {
      [POSTHOG_PROPERTIES.REFERRAL_CODE]: referralCode,
      [POSTHOG_PROPERTIES.SHARE_METHOD]: shareMethod,
    })
  },

  referralRewardClaimed: (referralCode, rewardAmount) => {
    PostHogTracker.track(POSTHOG_EVENTS.REFERRAL_REWARD_CLAIMED, {
      [POSTHOG_PROPERTIES.REFERRAL_CODE]: referralCode,
      reward_amount: rewardAmount,
    })
  },

  referredUserListViewed: (referralCount) => {
    PostHogTracker.track(POSTHOG_EVENTS.REFERRED_USER_LIST_VIEWED, {
      referral_count: referralCount,
    })
  },

  // ============ TAX FILING ============

  taxPlanSelected: (planName) => {
    PostHogTracker.track(POSTHOG_EVENTS.TAX_PLAN_SELECTED, {
      [POSTHOG_PROPERTIES.TAX_PLAN]: planName,
    })
  },

  taxFormStarted: (planName) => {
    PostHogTracker.track(POSTHOG_EVENTS.TAX_FORM_STARTED, {
      [POSTHOG_PROPERTIES.TAX_PLAN]: planName,
    })
  },

  taxDocumentUploaded: (documentType) => {
    PostHogTracker.track(POSTHOG_EVENTS.TAX_DOCUMENT_UPLOADED, {
      [POSTHOG_PROPERTIES.DOCUMENT_TYPE]: documentType,
    })
  },

  taxFilingSubmitted: (planName) => {
    PostHogTracker.track(POSTHOG_EVENTS.TAX_FILING_SUBMITTED, {
      [POSTHOG_PROPERTIES.TAX_PLAN]: planName,
    })
  },

  taxFilingSuccess: (planName) => {
    PostHogTracker.track(POSTHOG_EVENTS.TAX_FILING_SUCCESS, {
      [POSTHOG_PROPERTIES.TAX_PLAN]: planName,
    })
  },

  taxFilingFailed: (planName, reason) => {
    PostHogTracker.track(POSTHOG_EVENTS.TAX_FILING_FAILED, {
      [POSTHOG_PROPERTIES.TAX_PLAN]: planName,
      [POSTHOG_PROPERTIES.ERROR_MESSAGE]: reason,
    })
  },

  // ============ INSURANCE ============

  insurancePlanViewed: (insuranceType, provider = null) => {
    PostHogTracker.track(POSTHOG_EVENTS.INSURANCE_PLAN_VIEWED, {
      [POSTHOG_PROPERTIES.INSURANCE_TYPE]: insuranceType,
      [POSTHOG_PROPERTIES.PROVIDER]: provider,
    })
  },

  insuranceCallBooked: (insuranceType) => {
    PostHogTracker.track(POSTHOG_EVENTS.INSURANCE_CALL_BOOKED, {
      [POSTHOG_PROPERTIES.INSURANCE_TYPE]: insuranceType,
    })
  },

  insuranceLearnMoreClicked: (insuranceType) => {
    PostHogTracker.track(POSTHOG_EVENTS.INSURANCE_LEARN_MORE_CLICKED, {
      [POSTHOG_PROPERTIES.INSURANCE_TYPE]: insuranceType,
    })
  },

  dittoIntegrationLaunched: () => {
    PostHogTracker.track(POSTHOG_EVENTS.DITTO_INTEGRATION_LAUNCHED, {})
  },
}

export default PostHogTracker
